import React from 'react';
import { LabelIconRow } from '@features/appointment-labels';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import { Box } from '@mui/material';

import type { BlockedTimeOccurrence } from '../../../types/blockedTime';
import { formatTimeRange } from '../../../utils/dateTimeFormatters';
import type { CalendarEvent } from '../types';

type BlockedTimeCalendarEvent = CalendarEvent & {
  eventType: 'blocked_time';
  blockedTime: BlockedTimeOccurrence;
};

interface BlockedTimeEventComponentProps {
  event: BlockedTimeCalendarEvent;
  viewMode?: string;
}

export const BlockedTimeEventComponent: React.FC<BlockedTimeEventComponentProps> = ({
  event,
  viewMode,
}) => {
  const isMonth = viewMode === 'month';
  const isAllDay = event.blockedTime.allDay;
  const labels = event.blockedTime.labels;

  return (
    <Box
      data-calendar-blocked-time-event="true"
      sx={{
        height: '100%',
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75,
        overflow: 'hidden',
        borderRadius: '6px',
        px: 1,
        py: 0.75,
        boxSizing: 'border-box',
        cursor: 'pointer',
        color: ELIG_TOKENS.gray,
        backgroundColor: ELIG_TOKENS.grayTint,
        backgroundImage: `repeating-linear-gradient(135deg, ${ELIG_TOKENS.graySoft} 0, ${ELIG_TOKENS.graySoft} 2px, transparent 2px, transparent 9px)`,
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderLeft: `4px solid ${ELIG_TOKENS.gray}`,
        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.55)',
        '&:hover': {
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
          zIndex: 10,
        },
        '@container (height < 27px)': {
          py: 'clamp(1px, 15cqh, 6px)',
          lineHeight: 1.2,
        },
      }}
    >
      <EventBusyOutlined sx={{ fontSize: 16, flexShrink: 0, mt: '1px', opacity: 0.8 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 13,
            fontWeight: 650,
          }}
        >
          Unavailable
        </Box>
        {!isMonth && (
          <Box
            sx={{
              mt: 0.25,
              fontSize: 11,
              opacity: 0.85,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '@container (height < 27px)': { display: 'none' },
            }}
          >
            {isAllDay ? 'All day' : formatTimeRange(event.start, event.end)}
          </Box>
        )}
      </Box>
      {labels.length > 0 && (
        <Box sx={{ mt: '1px', '@container (height < 27px)': { display: 'none' } }}>
          <LabelIconRow labels={labels} max={2} size={13} />
        </Box>
      )}
    </Box>
  );
};
